
import { CountryUserConfig, VerificationConfig } from "@domain/localization/country";
import { CountryService } from "@domain/localization/country-service";

import { UserManager } from "@domain/user/user-manager";
import { isNotNil } from "@utils/assert";
import { observable } from "micro-observables";

export class CountryManager {
	private readonly _countryConfig = observable<CountryUserConfig | null>(null);
	readonly countryConfig = this._countryConfig.readOnly();

	private readonly _supportedCountries = observable<string[]>([]);
	supportedCountries = this._supportedCountries.readOnly();

	private readonly _countryUserConfig = observable<CountryUserConfig | null>(null);
	readonly countryUserConfig = this._countryUserConfig.readOnly();

	private _initialized = false;
	private _initializationPromise: Promise<void> | null = null;

	constructor(
		private readonly countryService: CountryService,
		private readonly userManager: UserManager,
	) {
	}

	async init(): Promise<void> {
		if (this._initialized) {
			return;
		}

		if (this._initializationPromise) {
			await this._initializationPromise;
			return;
		}

		this._initializationPromise = (async () => {
			await this.fetchSupportedCountries();

			this.userManager.user.subscribe(async _user => {
				if (isNotNil(_user?.country)) {
					await this.fetchCountryUserConfig(_user.country);
				}
			});

			this._initialized = true;
		})();

		try {
			await this._initializationPromise;
		} finally {
			this._initializationPromise = null;
		}
	}

	async fetchSupportedCountries(): Promise<void> {
		const supportedCountries = await this.countryService.getSupportedCountries();
		this._supportedCountries.set(supportedCountries);
	}

	async getCountryUserConfig(countryCode: string): Promise<void> {
		this._countryUserConfig.set(await this.countryService.getCountryUserConfig(countryCode));
	}

	async fetchCountryUserConfig(countryCode: string): Promise<void> {
		this._countryConfig.set(await this.countryService.getCountryUserConfig(countryCode));
	}

	async getVerificationConfig(countryCode: string): Promise<VerificationConfig> {
		return await this.countryService.getVerificationConfig(countryCode);
	}

	async getLocale(email: string): Promise<string> {
		return await this.countryService.getLocale(email);
	}
}
